import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const digits = (v) => v.replace(/\D/g, "").slice(0, 4);
const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-[#6E665D] mb-2 block">{children}</label>;

export const NewCellarForm = ({ needLogin }) => {
  const { login, enterCave } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", pin: "", pin2: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, fn = (v) => v) => (e) => setF({ ...f, [k]: fn(e.target.value) });

  const submit = async (e) => {
    e.preventDefault();
    if (f.pin !== f.pin2) return toast.error("The two codes are different");
    setBusy(true);
    try {
      if (needLogin) await login(f.email, f.password);
      const { data } = await api.post("/caves", { name: f.name, pin: f.pin });
      await enterCave(data.id);
      toast.success(`Cellar "${data.name}" created`);
      navigate("/setup");
    } catch (err) {
      toast.error(err?.response?.status === 403 ? "Only the owner can create a cellar" : errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="new-cellar-form">
      <div>
        <Label>New cellar name</Label>
        <Input required maxLength={60} value={f.name} onChange={set("name")} className="h-12 bg-[#161214]" data-testid="new-cellar-name-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>4-digit code</Label>
          <Input required value={f.pin} onChange={set("pin", digits)} inputMode="numeric" type="password" placeholder="••••" className="h-12 text-center tracking-[0.6em] font-mono bg-[#161214]" data-testid="new-cellar-pin-input" />
        </div>
        <div>
          <Label>Repeat code</Label>
          <Input required value={f.pin2} onChange={set("pin2", digits)} inputMode="numeric" type="password" placeholder="••••" className="h-12 text-center tracking-[0.6em] font-mono bg-[#161214]" data-testid="new-cellar-pin2-input" />
        </div>
      </div>
      {needLogin && (
        <div className="rounded-lg border border-[#2E252A] p-4 space-y-4">
          <p className="text-xs text-[#A89F91]">Only the owner can create a cellar. Confirm with your account.</p>
          <Input required type="email" value={f.email} onChange={set("email")} placeholder="Owner email" className="h-12 bg-[#161214]" data-testid="new-cellar-email-input" />
          <Input required type="password" value={f.password} onChange={set("password")} placeholder="Password" className="h-12 bg-[#161214]" data-testid="new-cellar-password-input" />
        </div>
      )}
      <Button type="submit" disabled={busy || f.pin.length !== 4 || f.pin2.length !== 4} className="w-full h-14 text-base bg-[#D4AF37] hover:bg-[#E5A93C] text-[#0D0A0B]" data-testid="new-cellar-submit-button">
        {busy ? "Creating…" : "Create and open the cellar"}
      </Button>
    </form>
  );
};

export const CellarModeTabs = ({ mode, setMode }) => (
  <div className="grid grid-cols-2 gap-1 rounded-lg border border-[#2E252A] bg-[#110D0F] p-1 mb-8" data-testid="cellar-mode-tabs">
    {[["existing", "Existing cellar"], ["new", "New Cellar"]].map(([k, label]) => (
      <button key={k} type="button" onClick={() => setMode(k)} data-testid={`cellar-mode-${k}`}
        className={`h-11 rounded-md text-sm transition-colors ${mode === k ? "bg-[#5C1D24] text-[#F5F0EB]" : "text-[#A89F91] hover:text-[#F5F0EB]"}`}>{label}</button>
    ))}
  </div>
);
